// TableCellComponents.tsx

import StatusBadge, {
  type StatusModule,
} from "@/app/components/common/badge/StatusBadge";
import type { BeneApproverProp } from "@/app/types/beneficiaries";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { maskString } from "@/app/utils/helpers";
import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
import ScheduleStatusBadge from "../badge/ScheduleStatusBadge";
import { gxDateFormat } from "@/app/utils/helpers/gxDateFormat";

// Base cell props that match GridCellProps structure
export interface BaseCellProps {
  dataItem: any;
  field?: string;
  rowType?: string;
  [key: string]: any;
}

// Name cell with avatar component (for generic use)
interface NameCellProps extends BaseCellProps {
  dataItem: {
    shortName: string;
    longName: string;
    [key: string]: any;
  };
}

export const NameCell: React.FC<NameCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-name">
  <div className="gx-cell-name-content">
    <div className="gx-cell-name-avatar">
      <span className="gx-p12-b">{dataItem.shortName}</span>
    </div>
    <div className="gx-cell-name-info ">
      <span className="gx-h6">{dataItem.longName}</span>
      <p className="gx-p12-m">
        Category : <span> Rent</span>
      </p>
    </div>
  </div>
  // </td>
);

// Initials cell with circular background (specific design)
interface InitialsCellProps extends BaseCellProps {
  dataItem: {
    shortForm: string;
    [key: string]: any;
  };
}

export const InitialsCell: React.FC<InitialsCellProps> = ({ dataItem }) => (
  // <td>
  <div className="gx-cell-initials-container">
    <span className="gx-cell-initials-text gx-font-bold">
      {" "}
      {/* font style (left inline) */}
      {dataItem.shortForm}
    </span>
  </div>
  // </td>
);

interface CompanyWithInitialsCellProps {
  dataItem: {
    shortForm: string;
    companyName: string;
    [key: string]: any;
  };
  approveRequired?: boolean;
}

export const CompanyWithInitialsCell: React.FC<
  CompanyWithInitialsCellProps
> = ({ dataItem, approveRequired = false }) => {
  return (
    // <td className="gx-cell-name">
    <div className="gx-cell-name-content">
      <div className="gx-cell-name-avatar">
        <span className="gx-p12-b">{dataItem.shortForm || "***"}</span>
      </div>
      <div className="gx-cell-name-info">
        <div title={dataItem.companyName}>
          <span className="gx-h6 line-clamp-1 gx-p12-b">
            {dataItem.companyName || dataItem?.debitAccountName}
          </span>
        </div>
        {approveRequired === true ? (
          <p className="gx-cell-date-text gx-p12-m">
            <MaskedAccountNumber
              accountNumber={dataItem?.debitAccountNo}
              maskClassName=" tracking-widest relative top-[5px]"
            />
          </p>
        ) : (
          dataItem?.paymentCategory && (
            <p className="gx-cell-payee-text-dataitem gx-p10-m">{`Category : ${dataItem?.paymentCategory}`}</p>
          )
        )}
      </div>
    </div>
    // </td>
  );
};

interface DebitAccountProp extends BaseCellProps {
  dataItem: {
    debitBankName: string;
    [key: string]: any;
  };
}

export const DebitBank: React.FC<DebitAccountProp> = ({ dataItem }) => {
  return (
    // <td className="gx-cell-name">
    <div className="gx-cell-name-content">
      <div className="gx-cell-name-info">
        <div title={dataItem.debitBankName}>
          <span className="gx-h6 line-clamp-1 gx-p12-b">
            {dataItem.debitBankName}
          </span>
        </div>
      </div>
    </div>
    // </td>
  );
};

// Company cell component
interface CompanyCellProps extends BaseCellProps {
  dataItem: {
    companyName: string;
    [key: string]: any;
  };
}

export const CompanyCell: React.FC<CompanyCellProps> = ({ dataItem }) => (
  <span className="gx-cell-company gx-p14-b"> {dataItem.companyName}</span>
);

// Define the allowed status types to match StatusBadge

interface StatusCellProps extends BaseCellProps {
  dataItem: {
    status?: number;
    paymentStatusCode?: number;
    recordStatusCodeDesc?: string;
    [key: string]: any;
  };
  module: StatusModule;
  statusField?: string;
}

// export const StatusCell: React.FC<StatusCellProps> = ({
//   dataItem,
//   module,
//   statusField,
// }) => {
//   const statusCode = statusField;
//   (statusField ? dataItem?.[statusField] : undefined) ??
//     dataItem?.paymentStatusCode ??
//     dataItem?.recordStatusCodeDesc ??
//     dataItem?.status ??
//     "text";

//   return <StatusBadge module={module} statusCode={statusCode} />;
// };

export const StatusCell: React.FC<StatusCellProps> = ({
  dataItem,
  module,
  statusField,
}) => {
  const statusCode =
    statusField && dataItem?.[statusField] !== undefined
      ? dataItem[statusField]
      : (dataItem?.paymentStatusCode ??
        dataItem?.recordStatusCodeDesc ??
        dataItem?.status);

  return <StatusBadge module={module} statusCode={statusCode} />;
};

export const StatusShedule: React.FC<StatusCellProps> = () => (
  <span className={`gx-p10-sb gx-status-badge`}>{"Scheduled"}</span>
);

interface StatustableCellProps extends BaseCellProps {
  dataItem: {
    status: number;
    [key: string]: any;
  };
}

export const StatustableCell: React.FC<StatustableCellProps> = ({
  dataItem,
}) => (
  // <td>
  <StatusBadge module="text" statusCode={dataItem.soExeFrequency} />
  // </td>
);

export const FrequencyCell: React.FC<StatustableCellProps> = ({ dataItem }) => (
  // <td>
  <span
    className="gx-status-badge gx-p10-sb bg-gray-100 text-gray-600 "
    data-overflowing="false"
  >
    <span className="status-text">{dataItem.soExeFrequency}</span>
  </span>
  // </td>
);

// Generic Amount cell component (uses currencyFormat)
interface AmountCellProps extends BaseCellProps {
  dataItem: {
    currencyFormat: string;
    Amount: number | string;
    [key: string]: any;
  };
  showDecimals?: boolean;
}

export const AmountCell: React.FC<AmountCellProps> = ({
  dataItem,
  showDecimals = true,
}) => (
  <div>
    <span className="gx-p12-b">
      {dataItem.currencyFormat}{" "}
      {typeof dataItem.Amount === "number"
        ? dataItem.Amount.toLocaleString()
        : dataItem.Amount}
    </span>
    {showDecimals && <span className="gx-p12-b">.00</span>}
  </div>
);

export interface CustomAmountCellProps extends BaseCellProps {
  dataItem: {
    countryCode?: string;
    Amount: number | string;
    [key: string]: any;
  };
  showDecimals?: boolean;
}

export const CustomAmountCell: React.FC<CustomAmountCellProps> = ({
  dataItem,
  showDecimals = true,
}) => {
  const amount =
    typeof dataItem?.Amount === "string"
      ? Number(dataItem.Amount)
      : dataItem?.Amount;

  const { integer, decimal, symbol } = gxCurrencyFormat(
    Number.isNaN(amount) ? undefined : amount,
    dataItem?.countryCode || "",
    dataItem?.baseCurrency || "",
  );

  return (
    <div className="amount-cell">
      {/* Currency */}
      <span className="amount-currency gx-p12-b">{symbol}</span>

      {/* Integer part */}
      <span className="amount-whole gx-p12-b">{integer}</span>

      {/* Decimal part */}
      {showDecimals && (
        <span className="amount-decimal gx-p12-m">{decimal || ".00"}</span>
      )}
    </div>
  );
};

// Date cell component
interface DateCellProps extends BaseCellProps {
  dataItem: {
    date: string;
    [key: string]: any;
  };
  gxDateFormat?: (date: string) => string;
}

export const DateCell: React.FC<DateCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <span className="gx-cell-date-text gx-p12-m ">
    {gxDateTimeFormat(dataItem.date)}
  </span>
  // </td>
);

interface TextCellCommon {
  dataItem: {
    paymentCategory: string;
    [key: string]: any;
  };
}

export const TextCellCommon: React.FC<TextCellCommon> = ({ dataItem }) => (
  // <td className="gx-cell-name">
  <div className="gx-cell-name-info">
    <span className="gx-h6">
      {dataItem.paymentCategory || dataItem.transaction}
    </span>
  </div>
  // </td>
);

// Generic text cell component
interface TextCellProps extends BaseCellProps {
  dataItem: Record<string, any>;
  field: string;
  className?: string;
  textClassName?: string;
}

export const TextCell: React.FC<TextCellProps> = ({
  dataItem,
  field,
  textClassName = "gx-cell-text-content gx-font-medium" /* font style (left inline) */,
}) => (
  // <td className={className}>
  <span className={textClassName}>{dataItem[field]}</span>
  // </td>
);

// Account number cell with masking functionality
interface AccountNumberCellProps extends BaseCellProps {
  dataItem: {
    accountNumber: string;
    [key: string]: any;
  };
  maskNumber?: boolean;
}

export const AccountNumberCell: React.FC<AccountNumberCellProps> = ({
  dataItem,
  maskNumber = true,
}) => {
  const maskAccountNumber = (accountNumber: string): string => {
    if (!accountNumber) return "";
    const last4Digits = accountNumber.slice(-4);
    return "*** *** *" + last4Digits;
  };

  return (
    // <td className="gx-cell-account">
    <span className="gx-cell-account-text gx-font-medium">
      {" "}
      {/* font style (left inline) */}
      {maskNumber
        ? maskAccountNumber(dataItem.accountNumber)
        : dataItem.accountNumber}
    </span>
    // </td>
  );
};

// Account cell using MaskedAccountNumber component
interface AccountCellProps extends BaseCellProps {
  dataItem: {
    accountNumber: string;
    [key: string]: any;
  };
}

export const AccountCell: React.FC<AccountCellProps> = ({ dataItem }) => (
  <div className="gx-cell-masked-account gx-font-regular">
    {/* font style (left inline) */}
    {dataItem.accountNumber === "N/A" || !dataItem.accountNumber ? (
      "---"
    ) : (
      <MaskedAccountNumber
        maskClassName=" tracking-widest relative top-[5px]"
        accountNumber={dataItem.accountNumber}
      />
    )}
  </div>
);

interface GrowXReferenceCellProps extends BaseCellProps {
  dataItem: {
    gxChnNo: number;
    [key: string]: any;
  };
}
export const GrowXReferenceCell: React.FC<GrowXReferenceCellProps> = ({
  dataItem,
}) => (
  <span className=" gx-font-regular gx-p12-b">
    {/* font style (left inline) */}
    {dataItem.gxChnNo}
  </span>
);

interface NameCellTwoProp {
  dataItem: {
    debitAccountName: string;
  };
}

export const NameCellTwo: React.FC<NameCellTwoProp> = ({ dataItem }) => {
  return (
    <div className="gx-cell-name-content">
      <div className="gx-cell-name-avatar">
        <span className=" gx-p12-b ">{dataItem.debitAccountName[0]}</span>
      </div>
      <div className="gx-cell-name-info ">
        <span className="gx-h6">{dataItem.debitAccountName}</span>
      </div>
    </div>
  );
};

export const PayeeCell: React.FC<any> = ({ dataItem }) => (
  <p className="gx-cell-payee-text gx-p12-m">
    {(dataItem?.beneAccountName ?? "") + " " + (dataItem?.beneNickname ?? "")}
  </p>
);

interface ApproverProp {
  dataItem: {
    numberOfLevels: number;
    numberOfApproved: number;
  };
}

export const ApproverCell: React.FC<ApproverProp> = ({ dataItem }) => (
  // <td className="gx-cell-approvers">
  <div className="gx-cell-approvers-container w-fit">
    <p className="gx-cell-approvers-text gx-p10-b">{`${dataItem?.numberOfApproved}/${dataItem?.numberOfLevels}`}</p>
  </div>
  // </td>
);

export const BeneApproverCell: React.FC<BeneApproverProp> = ({ dataItem }) => {
  return (
    // <td className="gx-cell-approvers">
    <div className="gx-cell-approvers-container w-fit">
      <p className="gx-cell-approvers-text gx-p10-b">{`${dataItem?.numberOfApproved}/${dataItem?.noOfApprLevel}`}</p>
    </div>
    // </td>
  );
};

export const NoOfApproverCell: React.FC<ApproverProp> = ({ dataItem }) => {
  return (
    // <td className="gx-cell-approvers">
    <div className="gx-cell-approvers-container w-fit">
      <p className="gx-cell-approvers-text gx-p10-b">{`${dataItem?.numberOfApproved}/${dataItem?.numberOfLevels}`}</p>
    </div>
    // </td>
  );
};

interface DateCellProps extends BaseCellProps {
  dataItem: {
    date: string;
    [key: string]: any;
  };
}

export const DateCellOne: React.FC<DateCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <span className="gx-cell-date-text gx-p10-m">
    {gxDateTimeFormat(dataItem?.crtdDt || dataItem?.uploadedOn)}
  </span>
  // </td>
);

export const DateCellTwo: React.FC<DateCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <span className="gx-cell-date-text gx-p12-m ml-1">
    {gxDateFormat(dataItem?.paymentDate)}
  </span>
  // </td>
);

export const DateCellThree: React.FC<DateCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <span className="gx-cell-date-text gx-p14-m">
    {gxDateTimeFormat(dataItem?.crtdDt)}
  </span>
  // </td>
);

export const RequestedDate: React.FC<DateCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <span className="gx-cell-date-text gx-p12-m">
    {gxDateTimeFormat(dataItem?.paymentDate)}
  </span>
  // </td>
);
interface AmountCellTwoProps {
  dataItem: {
    debitAmount: number;
    debitCurrency: string;
  };
}

export const AmountCellTwo: React.FC<AmountCellTwoProps> = ({ dataItem }) => {
  const { integer, decimal, symbol } = gxCurrencyFormat(
    dataItem?.debitAmount,
    dataItem?.debitCurrency,
    undefined,
    {
      showSymbol: false, // currency shown separatelyc
      showCode: false,
    },
  );

  return (
    <div className="amount-cell">
      <span className="amount-currency gx-p12-b">{symbol}</span>

      <span className="amount-whole gx-p12-b">{integer}</span>

      <span className="amount-decimal gx-p12-m">{decimal}</span>
    </div>
  );
};

export const RTNameCell: React.FC<NameCellProps> = ({ dataItem }) => {
  return (
    // <td className="gx-cell-name">
    <div className="gx-cell-name-content">
      <div className="gx-cell-name-avatar">
        <span className="gx-p12-b">{dataItem.companyName[0]}</span>
      </div>
      <div className="gx-cell-name-info ">
        <span className="gx-h6">{dataItem.companyName}</span>
        {/* <p className="gx-p12-m">
            Category : <span> Rent</span>
          </p> */}
      </div>
    </div>
    // </td>
  );
};

interface RereferenceCellProps {
  dataItem: {
    gxChnNo: string;
    cbsReferenceNo?: string;
    growxRefNo?: string;
    uploadedOn?: string;
  };
}

export const Rereference: React.FC<RereferenceCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <div>
    <span className="gx-cell-date-text gx-p12-m">
      {dataItem?.gxChnNo || dataItem?.growxRefNo || dataItem?.uploadedOn}
    </span>
    {dataItem?.cbsReferenceNo && (
      <div>
        <p className="gx-cell-date-text-p gx-p10-m">{`Bank Ref : ${dataItem?.cbsReferenceNo}`}</p>
      </div>
    )}
  </div>
  // </td>
);

export const Account: React.FC<AccountCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <div>
    <span className="gx-cell-date-text gx-p12-m">
      <MaskedAccountNumber accountNumber={dataItem?.debitAccountNo} />
    </span>
    {dataItem?.debitBankName &&<div>
      <p className="gx-cell-date-text-p gx-p10-m">{`Bank : ${dataItem?.debitBankName}`}</p>
    </div>}
  </div>
  // </td>
);

export const PortalAccount: React.FC<AccountCellProps> = ({ dataItem }) => (
  // <td className="gx-cell-date">
  <div>
    <span className="gx-cell-date-text gx-p12-m">
      {maskString(dataItem?.debitAccountNo)}
    </span>
    <div>
      <p className="gx-cell-date-text-p gx-p10-m">{`Bank : ${dataItem?.debitBankName}`}</p>
    </div>
  </div>
  // </td>
);

interface AmountWithTrendCellProps {
  dataItem: {
    Amount: number;
    countryCode?: string;
    transactionType?: "Credit" | "Debit";
  };
}
interface AmountWithTrendCellProps {
  dataItem: {
    Amount: number;
    countryCode?: string;
    transactionType?: "Credit" | "Debit";
  };
}

export const AmountWithTrendCell: React.FC<AmountWithTrendCellProps> = ({
  dataItem,
}) => {
  const isCredit = dataItem?.transactionType === "Credit";

  return (
    <div className="flex items-center justify-start gap-2">
      <img
        src={`${ICON_BASE_PATH}${isCredit ? "graph_up.svg" : "graph_down.svg"}`}
        alt={isCredit ? "up" : "down"}
      />

      {/* Amount – SAME STRUCTURE AS CustomAmountCell */}
      <CustomAmountCell dataItem={dataItem} showDecimals={true} />
    </div>
  );
};

export const Description: React.FC<{ description: string }> = ({
  description,
}) => (
  <span className="text-[#172477] text-[14px] gx-font-medium">
    {description}
  </span>
);

export const BeneficiaryName: React.FC<{ beneficiary: string }> = ({
  beneficiary,
}) => (
  <span className="text-[#222222] text-[14px] gx-font-medium">
    {beneficiary}
  </span>
);

export const Period: React.FC<{ startDate: string; endDate: string }> = ({
  startDate,
  endDate,
}) => (
  <span className="text-[#666666] text-[14px] gx-font-medium">
    {`${gxDateFormat(startDate)} - ${gxDateFormat(endDate)}`}
  </span>
);

export const NextPaymentDate: React.FC<{ nextPaymentDate: string }> = ({
  nextPaymentDate,
}) => (
  <span className="text-[#666666] text-[14px] gx-font-medium">
    {`${gxDateFormat(nextPaymentDate)}`}
  </span>
);

export const Frequency: React.FC<{ frequency: string }> = ({ frequency }) => (
  <span className="text-[#222222] text-[14px] gx-font-semibold">
    {frequency}
  </span>
);

export const ScheduledOn: React.FC<{ scheduledOnDate: string }> = ({
  scheduledOnDate,
}) => (
  <span className="text-[#666666] text-[14px] gx-font-medium">
    {gxDateFormat(scheduledOnDate)}
  </span>
);

export const Amount: React.FC<{ amount: number; currency: string }> = ({
  amount,
  currency,
}) => (
  <p className=" text-[14px] ">
    <span className="text-[#222222] gx-font-bold">
      {gxCurrencyFormat(amount, currency).symbol}{" "}
      {gxCurrencyFormat(amount, currency).integer}
    </span>
    <span className="text-[#A8A8A8] gx-font-medium">
      {gxCurrencyFormat(amount, currency).decimal}
    </span>
  </p>
);
